/**
 * Platform admin card: one on/off platform flag (chat streaming, analysis
 * streaming, agentic mentor chat). Presentational: the flag state and toggle
 * come from useFeatureToggle in the platform admin screen, which loads all
 * three flags with the rest of the platform data.
 */
import React from 'react';
import { ActivityIndicator, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Text } from '@/components/ui/text';

interface FeatureToggleCardProps {
  title: string;
  description: string;
  enableLabel: string;
  disableLabel: string;
  enabled: boolean;
  loading: boolean;
  onToggle: () => void;
}

export default function FeatureToggleCard({
  title,
  description,
  enableLabel,
  disableLabel,
  enabled,
  loading,
  onToggle,
}: FeatureToggleCardProps) {
  return (
    <Card>
      <CardHeader className="pb-0">
        <Text className="text-lg font-semibold text-foreground">
          {title}
        </Text>
        <Text className="text-sm text-muted-foreground mt-1">
          {description}
        </Text>
      </CardHeader>
      <CardContent className="gap-3">
        <View className="flex-row items-center justify-between">
          <Text className="text-sm text-foreground">
            Status: {enabled ? 'Enabled' : 'Disabled'}
          </Text>
          <Button
            variant={enabled ? 'destructive' : 'default'}
            disabled={loading}
            onPress={onToggle}
            className="flex-row items-center justify-center gap-2"
          >
            {loading ? (
              <ActivityIndicator size="small" color={enabled ? '#ffffff' : '#1F2937'} />
            ) : (
              <MaterialIcons
                name={enabled ? 'stop' : 'play-arrow'}
                size={18}
                color={enabled ? '#ffffff' : '#1F2937'}
              />
            )}
            <Text className={`text-sm font-semibold ${enabled ? 'text-white' : 'text-foreground'}`}>
              {enabled ? disableLabel : enableLabel}
            </Text>
          </Button>
        </View>
      </CardContent>
    </Card>
  );
}
